import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Printer, FileText, Pill, Calendar, Download, CalendarDays, ShieldAlert, Flame } from "lucide-react";
import DrillHistory from "@/components/DrillHistory";
import { toast } from "sonner";

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromISO = (s) => (s ? new Date(s + "T00:00:00") : undefined);
const fmtDisplay = (s) => {
  if (!s) return "Pick a date";
  const d = fromISO(s);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

function DatePopover({ value, onChange, testId, label }) {
  const selected = fromISO(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full mt-1 justify-start text-left font-normal"
          data-testid={testId}
          aria-label={label}
        >
          <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground" />
          {fmtDisplay(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <CalendarPicker
          mode="single"
          selected={selected}
          onSelect={(d) => d && onChange(toISO(d))}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function AttendanceReport({ report, onPrint }) {
  return (
    <Card className="p-8 max-w-4xl mx-auto bg-white">
      <div className="flex items-start justify-between mb-6 border-b border-border pb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">OPPO, LLC.</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">Attendance Log Report (Service Billing)</h2>
          <p className="text-xs text-muted-foreground mt-1">Generated {new Date(report.generated_at).toLocaleString()}</p>
        </div>
        <Button onClick={onPrint} className="no-print" data-testid="print-attendance"><Printer className="w-4 h-4 mr-2" /> Print</Button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
        <div><p className="text-label mb-0.5">Person's Name</p><p className="font-semibold text-base">{report.client.name}</p></div>
        <div><p className="text-label mb-0.5">Service Code</p><p className="font-mono font-bold text-base">HHS</p><p className="text-xs text-muted-foreground">DHHS Contractor Services</p></div>
        <div><p className="text-label mb-0.5">Contractor</p><p className="font-semibold">{report.contractor?.name || report.host_home?.name || "—"}</p></div>
        <div><p className="text-label mb-0.5">Date Range</p><p className="font-semibold">{report.start_date} → {report.end_date}</p></div>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-primary/5 border-y-2 border-primary">
            <th className="text-left py-2 px-2">Date</th>
            <th className="text-left py-2 px-2">Staff / Host</th>
            <th className="text-left py-2 px-2">Service Code</th>
            <th className="text-left py-2 px-2">Attendance</th>
            <th className="text-left py-2 px-2">Daily Note</th>
          </tr>
        </thead>
        <tbody>
          {report.notes.length === 0 && (<tr><td colSpan={5} className="text-center text-muted-foreground py-6">No notes in this date range.</td></tr>)}
          {report.notes.map((n) => (
            <tr key={n.id} className="border-b border-border align-top">
              <td className="py-2 px-2 font-medium whitespace-nowrap">{n.date}</td>
              <td className="py-2 px-2">{n.provider_name}</td>
              <td className="py-2 px-2 font-mono font-semibold">HHS</td>
              <td className="py-2 px-2 capitalize">{n.attendance}</td>
              <td className="py-2 px-2 text-xs whitespace-pre-wrap">{n.summary_note || <span className="text-muted-foreground">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function EmarReport({ report, onPrint }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const printGrid = () => {
    const d = new Date(report.start_date + "T00:00:00");
    const url = `${BACKEND_URL}/api/exports/emar-grid-pdf/${report.client.id}?year=${d.getFullYear()}&month=${d.getMonth() + 1}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <Card className="p-8 max-w-4xl mx-auto bg-white">
      <div className="flex items-start justify-between mb-6 border-b border-border pb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">OPPO, LLC.</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">eMAR Audit Report</h2>
          <p className="text-xs text-muted-foreground mt-1">Generated {new Date(report.generated_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={printGrid}
            className="no-print bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="print-emar-grid"
            title={`Open the Dispill 31-day grid PDF for ${report.client.name}`}
          >
            <Printer className="w-4 h-4 mr-2" /> Dispill 31-Day Grid PDF
          </Button>
          <Button onClick={onPrint} variant="outline" className="no-print" data-testid="print-emar">
            <Printer className="w-4 h-4 mr-2" /> Print View
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
        <div><p className="text-label mb-0.5">Person's Name</p><p className="font-semibold text-base">{report.client.name}</p></div>
        <div><p className="text-label mb-0.5">Contractor</p><p className="font-semibold">{report.contractor?.name || report.host_home?.name || "—"}</p></div>
        <div><p className="text-label mb-0.5">Date Range</p><p className="font-semibold">{report.start_date} → {report.end_date}</p></div>
        <div><p className="text-label mb-0.5">Service Code</p><p className="font-mono font-bold">HHS</p></div>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-primary/5 border-y-2 border-primary">
            <th className="text-left py-2 px-2">Date</th>
            <th className="text-left py-2 px-2">Medication</th>
            <th className="text-left py-2 px-2">Dosage</th>
            <th className="text-left py-2 px-2">AM</th>
            <th className="text-left py-2 px-2">PM</th>
            <th className="text-left py-2 px-2">Missed Reason</th>
            <th className="text-left py-2 px-2">Staff</th>
          </tr>
        </thead>
        <tbody>
          {(report.emar_logs || []).length === 0 && (<tr><td colSpan={7} className="text-center text-muted-foreground py-6">No eMAR data in this range.</td></tr>)}
          {(report.emar_logs || []).flatMap((log) =>
            (log.entries || []).map((e, i) => (
              <tr key={`${log.id}-${i}`} className="border-b border-border">
                <td className="py-2 px-2 font-medium whitespace-nowrap">{log.date}</td>
                <td className="py-2 px-2">{e.name}</td>
                <td className="py-2 px-2">{e.dosage}</td>
                <td className={`py-2 px-2 ${e.am_status === "missed" ? "text-red-700 font-bold" : ""}`}>{e.am_status}</td>
                <td className={`py-2 px-2 ${e.pm_status === "missed" ? "text-red-700 font-bold" : ""}`}>{e.pm_status}</td>
                <td className="py-2 px-2 text-xs italic">{(e.am_status === "missed" || e.pm_status === "missed") ? (e.missed_reason || "—") : ""}</td>
                <td className="py-2 px-2 text-xs">{log.provider_name}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {(report.emar_logs || []).some((l) => (l.prn_entries || []).length > 0) && (
        <div className="mt-6">
          <h3 className="font-display font-semibold border-b border-border pb-1 mb-2">PRN Administrations</h3>
          <table className="w-full border-collapse text-sm">
            <thead><tr className="bg-amber-50 border-y border-amber-200"><th className="text-left py-2 px-2">Date</th><th className="text-left py-2 px-2">Medication</th><th className="text-left py-2 px-2">Time</th><th className="text-left py-2 px-2">Reason</th><th className="text-left py-2 px-2">Staff</th></tr></thead>
            <tbody>
              {(report.emar_logs || []).flatMap((log) =>
                (log.prn_entries || []).map((p, i) => (
                  <tr key={`${log.id}-prn-${i}`} className="border-b border-border">
                    <td className="py-2 px-2">{log.date}</td>
                    <td className="py-2 px-2">{p.name} {p.dosage}</td>
                    <td className="py-2 px-2">{p.time || "—"}</td>
                    <td className="py-2 px-2 italic">{p.reason}</td>
                    <td className="py-2 px-2 text-xs">{log.provider_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function BehaviorIncidentsReport({ report, onPrint }) {
  const notifLabel = (k) => ({
    admin_notified: "Admin / SC notified",
    guardian_notified: "Family / Guardian notified",
    both_notified: "Admin + Guardian notified",
    no_notification_yet: "Pending notification",
    no_notification_needed: "No notification needed (de-escalated)",
  }[k] || k || "—");
  const incidents = report.incidents || [];
  return (
    <Card className="p-8 max-w-5xl mx-auto bg-white" data-testid="behavior-incidents-report">
      <div className="flex items-start justify-between mb-6 border-b border-border pb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">OPPO, LLC.</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">Behavior Incident Log</h2>
          <p className="text-xs text-muted-foreground mt-1">Every captured client-specific behavior escalation in this date range.</p>
        </div>
        <Button onClick={onPrint} className="no-print" data-testid="print-incidents"><Printer className="w-4 h-4 mr-2" /> Print</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
        <div><p className="text-label mb-0.5">Person Served</p><p className="font-semibold text-base">{report.client?.name || "—"}</p></div>
        <div><p className="text-label mb-0.5">Contractor</p><p className="font-semibold">{report.contractor?.name || "—"}</p></div>
        <div><p className="text-label mb-0.5">Date Range</p><p className="font-semibold">{report.start_date} → {report.end_date}</p></div>
        <div><p className="text-label mb-0.5">Total Incidents</p><p className="font-semibold">{incidents.length}</p></div>
      </div>
      {incidents.length === 0 ? (
        <div className="text-center text-muted-foreground py-12 border-2 border-dashed border-border rounded-md" data-testid="incidents-empty">
          <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <p className="font-semibold">No behavior incidents in this date range.</p>
          <p className="text-xs mt-1">Either everything went smoothly or no triggers fired on this client.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2" data-testid="incidents-list">
          {incidents.map((inc, idx) => (
            <div key={`${inc.date}-${idx}`} className="border-l-4 border-l-red-500 bg-red-50/40 rounded-md p-4 space-y-2" data-testid={`incident-row-${idx}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="font-display font-bold text-red-900 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" /> {inc.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {inc.date} · Logged by <span className="font-semibold text-foreground">{inc.provider_name}</span>
                  </p>
                </div>
                <span className="inline-flex items-center bg-red-100 text-red-800 border border-red-200 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full">
                  {notifLabel(inc.notification_status)}
                </span>
              </div>
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-label">Antecedent</p>
                  <p className="text-xs whitespace-pre-wrap mt-1">{inc.antecedent || "—"}</p>
                </div>
                <div>
                  <p className="text-label">Behavior details</p>
                  <p className="text-xs whitespace-pre-wrap mt-1">{inc.behavior_details || "—"}</p>
                </div>
                <div>
                  <p className="text-label">De-escalation</p>
                  <p className="text-xs whitespace-pre-wrap mt-1">{inc.de_escalation || "—"}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AppointmentsReport({ report, onPrint }) {  return (
    <Card className="p-8 max-w-4xl mx-auto bg-white">
      <div className="flex items-start justify-between mb-6 border-b border-border pb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">OPPO, LLC.</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">Medical & Dental Appointments History</h2>
          <p className="text-xs text-muted-foreground mt-1">Generated {new Date(report.generated_at).toLocaleString()}</p>
        </div>
        <Button onClick={onPrint} className="no-print" data-testid="print-appointments"><Printer className="w-4 h-4 mr-2" /> Print</Button>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
        <div><p className="text-label mb-0.5">Person's Name</p><p className="font-semibold text-base">{report.client.name}</p></div>
        <div><p className="text-label mb-0.5">Contractor</p><p className="font-semibold">{report.contractor?.name || report.host_home?.name || "—"}</p></div>
        <div><p className="text-label mb-0.5">Date Range</p><p className="font-semibold">{report.start_date} → {report.end_date}</p></div>
        <div><p className="text-label mb-0.5">Total Appointments</p><p className="font-semibold">{(report.appointments || []).length}</p></div>
      </div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-primary/5 border-y-2 border-primary">
            <th className="text-left py-2 px-2">Date</th>
            <th className="text-left py-2 px-2">Type</th>
            <th className="text-left py-2 px-2">Doctor / Clinic</th>
            <th className="text-left py-2 px-2">Important Details</th>
            <th className="text-left py-2 px-2">Follow-up</th>
            <th className="text-left py-2 px-2">Logged By</th>
          </tr>
        </thead>
        <tbody>
          {(report.appointments || []).length === 0 && (<tr><td colSpan={6} className="text-center text-muted-foreground py-6">No appointments in range.</td></tr>)}
          {(report.appointments || []).map((a) => (
            <tr key={a.id} className="border-b border-border align-top">
              <td className="py-2 px-2 font-medium whitespace-nowrap">{a.date}</td>
              <td className="py-2 px-2">{a.appt_type}</td>
              <td className="py-2 px-2">{a.doctor_clinic || "—"}</td>
              <td className="py-2 px-2 text-xs whitespace-pre-wrap">{a.important_details}</td>
              <td className="py-2 px-2 text-xs whitespace-pre-wrap">{a.follow_up || "—"}</td>
              <td className="py-2 px-2 text-xs">{a.provider_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function AdminAuditReport() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reportType, setReportType] = useState("attendance");
  const [report, setReport] = useState(null);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const generate = async (type) => {
    const t = type || reportType;
    if (!clientId || !start || !end) return toast.error("Select client and date range");
    try {
      let url;
      if (t === "appointments") url = `/audit-report-appointments?client_id=${clientId}&start_date=${start}&end_date=${end}`;
      else if (t === "incidents") url = `/audit-report-behavior-incidents?client_id=${clientId}&start_date=${start}&end_date=${end}`;
      else url = `/audit-report?client_id=${clientId}&start_date=${start}&end_date=${end}&report_type=${t}`;
      const { data } = await api.get(url);
      setReportType(t);
      setReport({ ...data, report_type: t });
    } catch (e) { toast.error("Could not generate report"); }
  };

  const doPrint = () => window.print();

  const downloadCsv = () => {
    if (!report) return;
    let rows = [];
    let filename = "audit-report.csv";
    if (report.report_type === "attendance") {
      rows.push(["Date", "Person's Name", "Service Code", "Staff/Host", "Attendance", "Overnight", "Daily Note"]);
      (report.notes || []).forEach((n) => rows.push([n.date, report.client.name, "HHS", n.provider_name, n.attendance, n.overnight_stayed ? "Yes" : "No", (n.summary_note || "").replace(/[\r\n]+/g, " ")]));
      filename = `attendance-${report.client.name.replace(/\s+/g, "_")}-${report.start_date}_${report.end_date}.csv`;
    } else if (report.report_type === "emar") {
      rows.push(["Date", "Person's Name", "Medication", "Dosage", "AM", "PM", "Missed Reason", "Staff"]);
      (report.emar_logs || []).forEach((log) => (log.entries || []).forEach((e) => rows.push([log.date, report.client.name, e.name, e.dosage, e.am_status, e.pm_status, (e.am_status === "missed" || e.pm_status === "missed") ? (e.missed_reason || "") : "", log.provider_name])));
      filename = `emar-${report.client.name.replace(/\s+/g, "_")}-${report.start_date}_${report.end_date}.csv`;
    } else if (report.report_type === "appointments") {
      rows.push(["Date", "Person's Name", "Type", "Doctor/Clinic", "Important Details", "Follow-up", "Logged By"]);
      (report.appointments || []).forEach((a) => rows.push([a.date, report.client.name, a.appt_type, a.doctor_clinic || "", (a.important_details || "").replace(/[\r\n]+/g, " "), (a.follow_up || "").replace(/[\r\n]+/g, " "), a.provider_name]));
      filename = `appointments-${report.client.name.replace(/\s+/g, "_")}-${report.start_date}_${report.end_date}.csv`;
    } else if (report.report_type === "incidents") {
      rows.push(["Date", "Person's Name", "Trigger", "Antecedent", "Behavior Details", "De-escalation", "Notification Status", "Logged By"]);
      (report.incidents || []).forEach((i) => rows.push([
        i.date, report.client?.name || "", i.label,
        (i.antecedent || "").replace(/[\r\n]+/g, " "),
        (i.behavior_details || "").replace(/[\r\n]+/g, " "),
        (i.de_escalation || "").replace(/[\r\n]+/g, " "),
        i.notification_status, i.provider_name,
      ]));
      filename = `behavior-incidents-${report.client.name.replace(/\s+/g, "_")}-${report.start_date}_${report.end_date}.csv`;
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async (kind) => {
    if (!clientId || !start || !end) return toast.error("Select client and date range first");
    const path = kind === "emar"
      ? `/exports/emar-pdf/${clientId}`
      : `/exports/dspd-notes-pdf/${clientId}`;
    try {
      const res = await api.get(`${path}?start_date=${start}&end_date=${end}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const c = clients.find((x) => x.id === clientId);
      a.href = url;
      a.download = `${kind === "emar" ? "emar" : "dspd-notes"}-${(c?.name || "client").replace(/\s+/g, "_")}-${start}_${end}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind === "emar" ? "eMAR" : "DSPD Notes"} PDF downloaded`);
    } catch (_e) {
      toast.error("PDF generation failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-audit">
      <div className="no-print">
        <p className="text-label">Billing</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">Audit Report</h1>
        <p className="text-sm text-muted-foreground">
          One client per report. Same filters apply to Attendance Log and eMAR exports.
        </p>
      </div>

      <Card className="p-5 no-print">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-label">Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="mt-1" data-testid="audit-client"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-label">Start Date</Label><DatePopover value={start} onChange={setStart} testId="audit-start" label="Start date" /></div>
          <div><Label className="text-label">End Date</Label><DatePopover value={end} onChange={setEnd} testId="audit-end" label="End date" /></div>
        </div>
        <Tabs value={reportType} onValueChange={(v) => { setReportType(v); setReport(null); }} className="mt-4">
          <TabsList>
            <TabsTrigger value="attendance" data-testid="audit-tab-attendance"><FileText className="w-4 h-4 mr-1.5" /> Attendance Log</TabsTrigger>
            <TabsTrigger value="emar" data-testid="audit-tab-emar"><Pill className="w-4 h-4 mr-1.5" /> eMAR Report</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="audit-tab-appointments"><Calendar className="w-4 h-4 mr-1.5" /> Appointments History</TabsTrigger>
            <TabsTrigger value="incidents" data-testid="audit-tab-incidents"><ShieldAlert className="w-4 h-4 mr-1.5" /> Behavior Incidents</TabsTrigger>
            <TabsTrigger value="drills" data-testid="audit-tab-drills"><Flame className="w-4 h-4 mr-1.5" /> Drill Logs</TabsTrigger>
          </TabsList>
          <TabsContent value="attendance" className="mt-3"><Button onClick={() => generate("attendance")} data-testid="audit-generate-attendance">Generate Attendance Report</Button></TabsContent>
          <TabsContent value="emar" className="mt-3"><Button onClick={() => generate("emar")} data-testid="audit-generate-emar">Generate eMAR Report</Button></TabsContent>
          <TabsContent value="appointments" className="mt-3"><Button onClick={() => generate("appointments")} data-testid="audit-generate-appointments">Generate Appointments History</Button></TabsContent>
          <TabsContent value="incidents" className="mt-3"><Button onClick={() => generate("incidents")} data-testid="audit-generate-incidents">Generate Behavior Incidents Log</Button></TabsContent>
          <TabsContent value="drills" className="mt-3">
            {clientId ? (
              <DrillHistory clientId={clientId} />
            ) : (
              <p className="text-sm text-muted-foreground">Select a client above to view their quarterly evacuation drill log.</p>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      <Card className="p-5 no-print bg-amber-50/40 border-amber-200" data-testid="dspd-exports">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-label text-amber-900">Utah DSPD State Audit Exports</p>
            <p className="text-sm">Audit-grade PDFs that compile the selected client's records for the date range above. Requires Client + Start + End filled in.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => downloadPdf("emar")} variant="outline" data-testid="export-emar-pdf">
              <Download className="w-4 h-4 mr-2" /> eMAR Clean Print PDF
            </Button>
            <Button onClick={() => downloadPdf("dspd-notes")} variant="outline" data-testid="export-dspd-pdf">
              <Download className="w-4 h-4 mr-2" /> DSPD Notes PDF
            </Button>
          </div>
        </div>
      </Card>

      {report && (
        <div className="print-page">
          <div className="max-w-4xl mx-auto mb-3 flex justify-end no-print">
            <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="download-csv">
              <Download className="w-4 h-4 mr-1.5" /> Download CSV
            </Button>
          </div>
          {report.report_type === "emar" && <EmarReport report={report} onPrint={doPrint} />}
          {report.report_type === "attendance" && <AttendanceReport report={report} onPrint={doPrint} />}
          {report.report_type === "appointments" && <AppointmentsReport report={report} onPrint={doPrint} />}
          {report.report_type === "incidents" && <BehaviorIncidentsReport report={report} onPrint={doPrint} />}
        </div>
      )}
    </div>
  );
}
